import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { startLoadingSucursalesDatafonos } from '../actions/pos';
import {
    RESOLUCION_CARGANDO,
    RESOLUCION_ERROR,
    RESOLUCION_RESUELTA,
} from '../helpers/cobroDatafono';

/**
 * EX-611: configuración de datáfonos de las sucursales de la empresa, para decidir si un cobro
 * con tarjeta va al terminal.
 *
 * Se lee del endpoint autenticado cada vez que se abre el modal que la necesita, y NO del store
 * de sucursales: el endpoint que puebla ese store es público y su proyección nunca traerá
 * `datafonos`. Leerla al abrir tiene además la ventaja de que la decisión se toma con la
 * configuración de ahora, no con la que hubiera al entrar al panel.
 *
 * Devuelve la resolución además de los datos, porque "esta sucursal no tiene terminal" y "no he
 * podido saberlo" llevan a cobros distintos y no pueden colapsar en una lista vacía.
 *
 * @param {boolean} activo - si la configuración hace falta ahora (el modal está abierto).
 */
export const useDatafonosSucursal = (activo) => {
    const dispatch = useDispatch();
    const [resolucion, setResolucion] = useState(RESOLUCION_CARGANDO);
    const [sucursales, setSucursales] = useState([]);
    // Solo manda la última petición pedida: una respuesta que llegue tarde (tras cerrar el modal
    // o tras un reintento) no puede pisar un estado más reciente.
    const peticionVigenteRef = useRef(0);

    const cargar = useCallback(async () => {
        const peticion = peticionVigenteRef.current + 1;
        peticionVigenteRef.current = peticion;
        setResolucion(RESOLUCION_CARGANDO);

        const respuesta = await dispatch(startLoadingSucursalesDatafonos());
        if (peticionVigenteRef.current !== peticion) return;

        if (!respuesta?.ok) {
            setSucursales([]);
            setResolucion(RESOLUCION_ERROR);
            return;
        }
        setSucursales(respuesta.sucursales || []);
        setResolucion(RESOLUCION_RESUELTA);
    }, [dispatch]);

    useEffect(() => {
        if (!activo) {
            peticionVigenteRef.current += 1;
            setSucursales([]);
            setResolucion(RESOLUCION_CARGANDO);
            return;
        }
        cargar();
    }, [activo, cargar]);

    return { resolucion, sucursales, reintentar: cargar };
};
